export function isJsonResponse(res: Response): boolean {
  const contentType = res.headers.get("content-type");
  if (contentType === null) return false;
  const mediaType = contentType.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  return mediaType === "application/json" || mediaType.endsWith("+json");
}

export type JsonFailureReason =
  /** fetch itself rejected: offline, DNS, TLS, aborted. */
  | "network"
  /** A non-OK status other than 401 and 404. */
  | "status"
  /** 200, but not JSON: the SPA fallback answered an API path. */
  | "not_json"
  /** The body claimed JSON, or parsed to a shape `parse` refused. */
  | "bad_json";

export type JsonResult<T> =
  | { kind: "ok"; data: T }
  | { kind: "unauthorized"; body: unknown }
  | { kind: "not_found"; body: unknown }
  | {
      kind: "error";
      reason: JsonFailureReason;
      status: number | null;
      body: unknown;
    };

async function readErrorBody(res: Response): Promise<unknown> {
  if (!isJsonResponse(res)) return null;
  return res.json().catch(() => null);
}

export async function fetchJsonResult<T>(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  parse: (body: unknown) => T,
): Promise<JsonResult<T>> {
  let res: Response;
  try {
    res = await fetch(input, init);
  } catch {
    return { kind: "error", reason: "network", status: null, body: null };
  }
  if (res.status === 401) {
    return { kind: "unauthorized", body: await readErrorBody(res) };
  }
  if (res.status === 404) {
    return { kind: "not_found", body: await readErrorBody(res) };
  }
  if (!res.ok) {
    return {
      kind: "error",
      reason: "status",
      status: res.status,
      body: await readErrorBody(res),
    };
  }
  // Checked before parsing: a 200 that is not JSON is the SPA shell, which is
  // a routing fact worth reporting as itself rather than as a parse failure.
  if (!isJsonResponse(res)) {
    return {
      kind: "error",
      reason: "not_json",
      status: res.status,
      body: null,
    };
  }
  try {
    return { kind: "ok", data: parse(await res.json()) };
  } catch {
    return {
      kind: "error",
      reason: "bad_json",
      status: res.status,
      body: null,
    };
  }
}
